package one.devsbox.www.ui.resources

import androidx.compose.animation.Crossfade
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridItemSpan
import androidx.compose.foundation.lazy.staggeredgrid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.*
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.files.ALL
import one.devsbox.shared.files.ResourceType
import one.devsbox.shared.files.RotationType
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.CategorySplashUI
import one.devsbox.www.ui.widgets.ContentActions
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.ui.widgets.SearchBox
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

data class ArgsListingResource(var pickerModeKey: String = "")

@Composable
@ComposableNavNode(ArgsListingResource::class)
fun ListingResourcesUI() {
    with(ListingResourcesCtx.get()) {
        ContentListingResourceUI(Modifier.fillMaxSize())
    }
}

@Composable
context(ctx: ListingResourcesCtx)
private fun ContentListingResourceUI(modifier: Modifier = Modifier) {
    Box(
        modifier = Modifier.fillMaxSize()
            .padding(16.dp)
    ) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    val resultData = state.nnvl
                    if (resultData.count == 0) {
                        CategorySplashUI(
                            icon = Svg.MediaOutline,
                            title = Res.string.ui_resources_splash_title,
                            explanation = Res.string.ui_resources_splash_desc,
                            actions = ctx.vm.contentActions
                        )
                    } else {
                        SuccessUI(resultData)
                        ContentActions(
                            contentActions = ctx.vm.contentActions,
                            colorInactive = ctx.colorSecondaryContainer,
                            colorActive = ctx.colorPrimaryContainer
                        )
                    }
                }
            }
        }
    }
}

@Composable
context(ctx: ListingResourcesCtx)
private fun SuccessUI(data: SharedIO.Resources) {
    val filteredItems = remember(data.items, ctx.vm.search.text) {
        data.items.filter {
            it.name.contains(ctx.vm.search.text, ignoreCase = true)
        }.sortedByDescending { it.create }
    }

    val optAll = stringResource(Res.string.ui_resources_listing_filter_opt_all)
    val optHorizontal = stringResource(Res.string.ui_resources_listing_filter_opt_horizontal)
    val optVertical = stringResource(Res.string.ui_resources_listing_filter_opt_vertical)
    val optImage = stringResource(Res.string.ui_resources_listing_filter_opt_image)
    val optVideo = stringResource(Res.string.ui_resources_listing_filter_opt_video)

    Column(
        modifier = Modifier.fillMaxSize(),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (ctx.args.pickerModeKey.isNotEmpty()) {
                FloatingActionButton(
                    shape = CircleShape,
                    onClick = { ctx.nav.up() },
                    containerColor = Color.Transparent,
                    contentColor = MaterialTheme.colorScheme.onSurface
                ) {
                    Icon(
                        imageVector = Svg.ArrowLeft,
                        contentDescription = "up nav"
                    )
                }
            }
            SearchBox(
                state = ctx.vm.search,
                hint =
                    if (ctx.args.pickerModeKey.isNotEmpty()) stringResource(Res.string.ui_resources_listing_search_pick_placeholder)
                    else stringResource(Res.string.ui_resources_listing_search_placeholder),
                modifier = Modifier.weight(1f),
                actions = listOf(
                    {
                        FilterDropdown(
                            modifier = Modifier.width(192.dp)
                                .align(Alignment.CenterVertically),
                            label = stringResource(Res.string.ui_resources_listing_filter_orientation),
                            selected = ctx.vm.selectedOrientation,
                            options = listOf(
                                RotationType.ALL to (optAll to Svg.RotateOutline),
                                RotationType.HORIZONTAL.mask to (optHorizontal to Svg.DisplayHorizontalOutline),
                                RotationType.VERTICAL.mask to (optVertical to Svg.DisplayVerticalOutline)
                            ),
                            onSelected = { ctx.action.setOrientation(it) }
                        )
                    },
                    {
                        FilterDropdown(
                            modifier = Modifier.width(128.dp)
                                .align(Alignment.CenterVertically),
                            label = stringResource(Res.string.ui_resources_listing_filter_type),
                            selected = ctx.vm.selectedMediaType,
                            options = listOf(
                                ResourceType.ALL to (optAll to Svg.MediaOutline),
                                ResourceType.IMAGE.mask to (optImage to Svg.ImageOutline),
                                ResourceType.VIDEO.mask to (optVideo to Svg.VideoOutline)
                            ),
                            onSelected = { ctx.action.setMediaType(it) }
                        )
                    }
                )
            )
        }

        val showingStart = if (filteredItems.isEmpty()) 0 else 1
        val showingEnd = filteredItems.size
        val totalCount = data.count

        Text(
            text = stringResource(
                Res.string.ui_resources_listing_showing,
                showingStart,
                showingEnd,
                totalCount
            ),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
            letterSpacing = 1.sp,
            modifier = Modifier.padding(bottom = 8.dp)
        )

        if (filteredItems.isEmpty()) {
            Box(
                modifier = Modifier.fillMaxWidth().weight(1f),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Icon(
                        imageVector = Svg.ImageOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(Modifier.height(16.dp))
                    Text(
                        text = stringResource(Res.string.ui_resources_listing_no_resources_found),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                    )
                }
            }
        } else {
            LazyVerticalStaggeredGrid(
                columns = StaggeredGridCells.Adaptive(240.dp),
                modifier = Modifier.fillMaxSize().weight(1f),
                contentPadding = PaddingValues(bottom = 250.dp),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalItemSpacing = 16.dp
            ) {
                items(filteredItems, key = { it.id }) { media ->
                    MediaCard(media) {
                        ctx.action.itemSelected(media)
                    }
                }

                if (filteredItems.size < data.count) {
                    item(span = StaggeredGridItemSpan.FullLine) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 32.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            if (ctx.vm.loadingMore) {
                                CircularProgressIndicator()
                            } else {
                                OutlinedButton(
                                    onClick = { ctx.action.loadMore() },
                                    shape = RoundedCornerShape(4.dp),
                                    colors = ButtonDefaults.outlinedButtonColors(
                                        contentColor = MaterialTheme.colorScheme.primary
                                    ),
                                    border = BorderStroke(
                                        1.dp,
                                        MaterialTheme.colorScheme.outlineVariant
                                    ),
                                    modifier = Modifier.width(200.dp).height(48.dp)
                                ) {
                                    Text(
                                        stringResource(Res.string.ui_users_listing_btn_load_more),
                                        fontSize = 13.sp,
                                        fontWeight = FontWeight.Bold
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun FilterDropdown(
    label: String,
    selected: Int,
    options: List<Pair<Int, Pair<String, ImageVector>>>,
    onSelected: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    var expanded by remember { mutableStateOf(false) }
    val rotation by animateFloatAsState(if (expanded) -90f else 90f)

    val current = options.firstOrNull { it.first == selected } ?: options.first()
    val currentLabel = current.second.first
    val currentIcon = current.second.second

    Box(modifier) {
        OutlinedButton(
            onClick = { expanded = true },
            shape = MaterialTheme.shapes.large,
            contentPadding = PaddingValues(horizontal = 12.dp, vertical = 8.dp),
            border = BorderStroke(1.dp, Color.Transparent),
            modifier = Modifier.height(48.dp)
        ) {
            Icon(
                imageVector = currentIcon,
                contentDescription = null,
                modifier = Modifier.size(18.dp),
                tint = MaterialTheme.colorScheme.onSurface
            )
            Spacer(Modifier.width(8.dp))
            Text(
                text = if (selected != options.first().first) currentLabel else label,
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.onSurface
            )
            Spacer(Modifier.weight(1F))
            Icon(
                imageVector = Svg.ChevronRight,
                contentDescription = null,
                modifier = Modifier.size(12.dp).rotate(rotation),
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.background(MaterialTheme.colorScheme.surface)
        ) {
            options.forEach { (optionValue, data) ->
                val (optionLabel, icon) = data
                DropdownMenuItem(
                    text = {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                imageVector = icon,
                                contentDescription = null,
                                modifier = Modifier.size(18.dp),
                                tint = if (selected == optionValue) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Spacer(Modifier.width(12.dp))
                            Text(
                                text = optionLabel,
                                color = if (selected == optionValue) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurface
                            )
                        }
                    },
                    onClick = {
                        onSelected(optionValue)
                        expanded = false
                    }
                )
            }
        }
    }
}
